/**
 * @file Header-field + reference review surface for a whole-document mapping
 * review. One diff row per `FieldPlan` (the chosen fact's label/value, the
 * shared `MappingReviewConfidenceTag` — a "manual" badge that still carries
 * the AI's score for the chosen fact once the user re-picked it or edited the
 * value — a value editor or fact re-picker, and a current-vs-proposed diff
 * against the bound document) and one per `ReferencePlan` (the
 * resolution display + a `ReferenceSelect` override; a `manual` resolution is
 * surfaced as unresolved with a warn Tag and the picker as the search-to-
 * resolve affordance). The current bound-document value is read from the
 * singleton `formStore` when the user is still on the bound form-version page,
 * or the keyed `useEntityRecordStore` for record bindings; proposed-only with
 * a notice otherwise. Add-field / add-reference Popovers draw their universe
 * from `useTargetFields`. Snapshots with `sync: true` — the editors are
 * controlled inputs bound to the session proxy, and every editor freezes via
 * `useMappingReviewSubmitting` while the approval is being written, closing the
 * post-snapshot data-loss window.
 */
import { FC, useContext, useState } from 'react';
import {
  Button,
  Input,
  InputNumber,
  Popover,
  Select,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { PlusOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { coerceCellValue } from '#types';
import type {
  ArCellValue,
  ArEntityReference,
  MappingTarget,
  ProcessWholeDocument,
  TableReferenceValueResolution,
} from '#types';
import { formStore, useEntityRecordStore } from '@/store';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import ReferenceSelect from '@/components/ArTable/components/ArCell/ReferenceSelect';
import { applyCellCleaningRules } from './applyCellCleaningRules';
import { formatCoercedValue } from './formatCoercedValue';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import {
  addField,
  addReference,
  isWholeDocument,
  removeField,
  removeReference,
  restoreField,
  restoreReference,
  setFieldFact,
  setFieldValue,
  setReferenceFact,
  setReferenceResolution,
} from './mappingReviewSession';
import type { MappingReviewSession } from './mappingReviewState';
import MappingReviewConfidenceTag from './MappingReviewConfidenceTag';
import { mappingReviewLabels } from './mappingReviewLabels';
import { mappingReviewRankedOptions } from './mappingReviewRankedOptions';
import { useMappingReviewSubmitting } from './useMappingReviewSubmitting';
import { useTargetFields } from './useTargetFields';

const { Text, Title } = Typography;

/** Shared grid for the Fields and References diff tables — fixed label/confidence/action tracks plus flexible value tracks; one `1fr`-bearing template keeps every row and the header aligned to the container width. */
const DIFF_GRID =
  '160px minmax(100px, 0.8fr) minmax(200px, 1.3fr) minmax(350px, 1.2fr) 130px 40px';

const DiffHeader: FC<{ first: string; proposed: string }> = ({
  first,
  proposed,
}) => {
  const { token } = theme.useToken();
  const style = { fontSize: token.fontSizeSM } as const;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: DIFF_GRID,
        columnGap: 10,
        alignItems: 'center',
        paddingInline: 5,
        paddingBottom: 5,
      }}
    >
      <Text strong style={style}>
        {first}
      </Text>
      <Text strong style={style}>
        Current
      </Text>
      <Text strong style={style}>
        {proposed}
      </Text>
      <Text strong style={style}>
        Source value
      </Text>
      <Text strong style={style}>
        Confidence
      </Text>
      <span />
    </div>
  );
};

function isEntityCellValue(value: ArCellValue): value is ArEntityReference {
  return (
    value != null &&
    typeof value === 'object' &&
    'referenceType' in value &&
    (value as ArEntityReference).referenceType === 'entity'
  );
}

function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

function resolutionToCellValue(
  resolution: TableReferenceValueResolution,
): ArCellValue {
  if (resolution.kind === 'entity') {
    return {
      referenceType: 'entity',
      docId: resolution.docId,
      ...(resolution.cacheLabel ? { cacheLabel: resolution.cacheLabel } : {}),
    };
  }
  return null;
}

const KeepRejectToggle: FC<{
  removed: boolean;
  label: string;
  disabled: boolean;
  onRemove: () => void;
  onRestore: () => void;
}> = ({ removed, label, disabled, onRemove, onRestore }) => (
  <Button
    type="text"
    size="small"
    disabled={disabled}
    icon={removed ? <UndoOutlined /> : <StopOutlined />}
    aria-label={
      removed
        ? `${mappingReviewLabels.restore} ${label}`
        : `${mappingReviewLabels.skip} ${label}`
    }
    onClick={() => (removed ? onRestore() : onRemove())}
  />
);

const CurrentValue: FC<{ current: unknown; changed: boolean }> = ({
  current,
  changed,
}) => {
  if (isEmptyValue(current))
    return (
      <Text italic type="secondary">
        empty
      </Text>
    );
  return (
    <Text delete={changed} type="secondary">
      {String(current)}
    </Text>
  );
};

const RequiredMark: FC = () => (
  <Tooltip title="Required field">
    <Text type="danger" aria-label="Required field">
      *
    </Text>
  </Tooltip>
);

interface MappingReviewFieldsProps {
  session: MappingReviewSession;
  target: Extract<MappingTarget, { kind: 'document' }>;
}

const MappingReviewFields: FC<MappingReviewFieldsProps> = ({
  session,
  target,
}) => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const submitting = useMappingReviewSubmitting();
  const snap = useSnapshot(session, { sync: true });
  const formSnap = useSnapshot(formStore.state);
  const { fields: fieldUniverse, references: referenceUniverse } =
    useTargetFields(target);
  const [addFieldId, setAddFieldId] = useState<string | undefined>(undefined);
  const [addRefSlot, setAddRefSlot] = useState<string | undefined>(undefined);

  const binding = target.binding;
  const boundVersionId =
    binding.kind === 'formVersion' ? binding.formVersionId : undefined;
  const boundEntityId =
    binding.kind === 'record' ? binding.entityId : undefined;
  const boundRecordId =
    binding.kind === 'record' ? binding.recordId : undefined;
  const recordStore = useEntityRecordStore(boundEntityId ?? '', boundRecordId);
  const formBound =
    Boolean(boundVersionId) && formSnap.versionId === boundVersionId;
  const recordBound =
    Boolean(boundRecordId) && recordStore.entityRecord.data !== null;
  const bound = formBound || recordBound;
  const currentFields = formBound
    ? formSnap.formVersion.data?.fields
    : recordBound
      ? recordStore.entityRecord.data?.fields
      : undefined;
  const currentRefs = formBound
    ? formSnap.formVersion.data?.entityReferences
    : recordBound
      ? recordStore.entityRecord.data?.entityReferences
      : undefined;

  const baseDoc = session.baseDoc;
  const wholeDoc: ProcessWholeDocument | null =
    baseDoc && isWholeDocument(baseDoc) ? baseDoc : null;
  const facts = wholeDoc?.facts ?? [];
  const fieldLabel = (id: string): string =>
    fieldUniverse.find((f) => f.id === id)?.label ?? id;
  const factOptions = facts.map((f, index) => ({
    value: index,
    label: `${f.label}: ${f.value}`,
  }));

  const requiredFieldIds = new Set(
    fieldUniverse.filter((f) => f.required).map((f) => f.id),
  );
  const requiredSlots = new Set(
    referenceUniverse.filter((s) => s.required).map((s) => s.slotKey),
  );
  const fields = [...Object.values(snap.fields)].sort(
    (a, b) =>
      Number(requiredFieldIds.has(b.targetField)) -
      Number(requiredFieldIds.has(a.targetField)),
  );
  const references = [...Object.values(snap.references)].sort(
    (a, b) =>
      Number(requiredSlots.has(b.slotKey)) -
      Number(requiredSlots.has(a.slotKey)),
  );
  const proposedFieldIds = new Set(fields.map((f) => f.targetField));
  const proposedSlots = new Set(references.map((r) => r.slotKey));
  const fieldPlan = (targetField: string) =>
    wholeDoc?.fields.find((p) => p.targetField === targetField);
  const refPlan = (slotKey: string) =>
    wholeDoc?.references.find((p) => p.slotKey === slotKey);
  const planConfidence = (factIndex: number): number | undefined =>
    wholeDoc?.fields.find((p) => p.factIndex === factIndex)?.confidence;
  const refPlanConfidence = (slotKey: string): number | undefined =>
    refPlan(slotKey)?.confidence;

  /** Scored fact picks for a header field: plan winner + runners-up, or the unmapped-field ranking for an added field. */
  const fieldFactScores = (
    targetField: string,
  ): { value: number; probability: number }[] => {
    const plan = fieldPlan(targetField);
    if (plan) {
      return [
        { value: plan.factIndex, probability: plan.confidence },
        ...plan.alternatives.map((a) => ({
          value: a.factIndex,
          probability: a.probability,
        })),
      ];
    }
    return (wholeDoc?.unmappedFields[targetField] ?? []).map((a) => ({
      value: a.factIndex,
      probability: a.probability,
    }));
  };

  /** Scored fact picks for a reference slot: plan winner + runners-up, or the unmapped-reference ranking for an added slot. */
  const refFactScores = (
    slotKey: string,
  ): { value: number; probability: number }[] => {
    const plan = refPlan(slotKey);
    if (plan) {
      return [
        { value: plan.factIndex, probability: plan.confidence },
        ...plan.alternatives.map((a) => ({
          value: a.factIndex,
          probability: a.probability,
        })),
      ];
    }
    return (wholeDoc?.unmappedReferences[slotKey] ?? []).map((a) => ({
      value: a.factIndex,
      probability: a.probability,
    }));
  };

  return (
    <FlexCol gap={10} className="mapping-review-fields">
      {!bound && (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Current document values unavailable — showing proposed values only.
        </Text>
      )}
      <FlexBox noGrow justifyStart>
        <Title level={5} style={{ margin: 0 }}>
          Fields
        </Title>
        <Popover
          trigger="click"
          title="Add field"
          content={
            <FlexCol
              gap={5}
              style={{
                width: 360,
                maxWidth: `calc(100vw - ${token.marginLG * 2}px)`,
              }}
            >
              <Select
                size="small"
                showSearch={{ optionFilterProp: 'label' }}
                placeholder="Field"
                aria-label="Add field to proposed writes"
                value={addFieldId}
                options={mappingReviewRankedOptions({
                  scored: Object.entries(wholeDoc?.unmappedFields ?? {})
                    .filter(([id]) => !proposedFieldIds.has(id))
                    .map(([id, alts]) => ({
                      value: id,
                      probability: Math.max(
                        ...alts.map((a) => a.probability),
                        0,
                      ),
                    })),
                  universe: fieldUniverse.map((f) => ({
                    value: f.id,
                    label: proposedFieldIds.has(f.id)
                      ? `${f.label} (proposed)`
                      : f.label,
                    disabled: proposedFieldIds.has(f.id),
                  })),
                  universeLabel: 'All fields',
                })}
                onChange={setAddFieldId}
              />
              <Button
                size="small"
                type="primary"
                disabled={!addFieldId}
                aria-label="Confirm add field"
                onClick={() => {
                  if (!addFieldId) return;
                  addField(addFieldId, '');
                  setAddFieldId(undefined);
                }}
              >
                Add
              </Button>
            </FlexCol>
          }
        >
          <Button
            size="small"
            icon={<PlusOutlined />}
            aria-label="Add field"
            disabled={submitting}
          >
            Add field
          </Button>
        </Popover>
      </FlexBox>
      <div style={{ overflowX: 'auto' }}>
        <FlexCol noGrow noGap>
          <DiffHeader first="Field" proposed="Proposed value" />
          {fields.map((field) => {
            const current = currentFields?.[field.targetField]?.value;
            const proposed = field.value;
            const changed = String(current ?? '') !== String(proposed ?? '');
            const overwrite = !isEmptyValue(current) && changed;
            const fact =
              field.factIndex >= 0 ? facts[field.factIndex] : undefined;
            const dataTypeId = fieldTypes[field.targetField]?.fieldDataTypeId;
            const options = fieldTypes[field.targetField]?.props?.options;
            const cleanedProposed = applyCellCleaningRules(
              proposed,
              fieldPlan(field.targetField)?.rules ?? [],
              snap.locale,
              snap.dateLocale,
              snap.centuryPivot,
              snap.dateZone,
            );
            const coerced = coerceCellValue(
              cleanedProposed,
              dataTypeId,
              {
                numberLocale: snap.locale,
                dateLocale: snap.dateLocale,
                centuryPivot: snap.centuryPivot,
                dateZone: snap.dateZone,
              },
              options ? { options } : undefined,
            );
            const coercedText = formatCoercedValue(coerced.value);
            const coercedPreview = coerced.lost ? (
              <Text type="warning" style={{ fontSize: token.fontSizeSM }}>
                won&apos;t import
              </Text>
            ) : coerced.value !== null &&
              coercedText !== String(proposed ?? '') ? (
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                → {coercedText}
              </Text>
            ) : null;
            return (
              <div
                key={field.targetField}
                style={{
                  display: 'grid',
                  gridTemplateColumns: DIFF_GRID,
                  columnGap: 10,
                  alignItems: 'start',
                  padding: 5,
                  borderRadius: token.borderRadius,
                  background:
                    overwrite && !field.removed
                      ? token.colorWarningBg
                      : undefined,
                }}
              >
                <FlexCol noGap noGrow>
                  <FlexBox noGrow justifyStart gap={5} alignStart>
                    <Text>{fieldLabel(field.targetField)}</Text>
                    {requiredFieldIds.has(field.targetField) && (
                      <RequiredMark />
                    )}
                  </FlexBox>
                  {fact && (
                    <Tooltip title={`from "${fact.label}: ${fact.value}"`}>
                      <Text
                        type="secondary"
                        style={{ fontSize: token.fontSizeSM }}
                      >
                        {fact.label}
                      </Text>
                    </Tooltip>
                  )}
                </FlexCol>
                <div>
                  <CurrentValue
                    current={current}
                    changed={changed && !field.removed}
                  />
                </div>
                <div>
                  {field.removed ? (
                    <Text delete type="secondary">
                      {String(proposed ?? '')}
                    </Text>
                  ) : (
                    <FlexCol noGap noGrow>
                      {typeof proposed === 'number' ? (
                        <InputNumber
                          size="small"
                          style={{ width: '100%' }}
                          value={proposed}
                          disabled={submitting}
                          aria-label={`Edit proposed value for ${fieldLabel(field.targetField)}`}
                          onChange={(value) =>
                            setFieldValue(field.targetField, value)
                          }
                        />
                      ) : (
                        <Input
                          size="small"
                          style={{ width: '100%' }}
                          value={proposed == null ? '' : String(proposed)}
                          disabled={submitting}
                          aria-label={`Edit proposed value for ${fieldLabel(field.targetField)}`}
                          onChange={(e) =>
                            setFieldValue(field.targetField, e.target.value)
                          }
                        />
                      )}
                      {coercedPreview}
                    </FlexCol>
                  )}
                </div>
                <div>
                  {factOptions.length > 0 && !field.removed && (
                    <Select
                      size="small"
                      showSearch={{ optionFilterProp: 'label' }}
                      placeholder={mappingReviewLabels.rePickValue}
                      aria-label={`Re-pick source ${mappingReviewLabels.extractedValue} for ${fieldLabel(field.targetField)}`}
                      value={field.factIndex >= 0 ? field.factIndex : undefined}
                      disabled={submitting}
                      options={mappingReviewRankedOptions({
                        scored: fieldFactScores(field.targetField),
                        universe: factOptions,
                        universeLabel: mappingReviewLabels.allValues,
                      })}
                      onChange={(value: number) =>
                        setFieldFact(field.targetField, value)
                      }
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
                <div>
                  <MappingReviewConfidenceTag
                    manual={field.manual}
                    confidence={planConfidence(field.factIndex)}
                    aiScore={
                      field.manual
                        ? fieldFactScores(field.targetField).find(
                            (s) => s.value === field.factIndex,
                          )?.probability
                        : undefined
                    }
                  />
                </div>
                <div>
                  <KeepRejectToggle
                    removed={field.removed}
                    label={`field ${fieldLabel(field.targetField)}`}
                    disabled={submitting}
                    onRemove={() => removeField(field.targetField)}
                    onRestore={() => restoreField(field.targetField)}
                  />
                </div>
              </div>
            );
          })}
        </FlexCol>
      </div>
      <FlexBox noGrow justifyStart>
        <Title level={5} style={{ margin: 0 }}>
          References
        </Title>
        <Popover
          trigger="click"
          title="Add reference"
          content={
            <FlexCol
              gap={5}
              style={{
                width: 360,
                maxWidth: `calc(100vw - ${token.marginLG * 2}px)`,
              }}
            >
              <Select
                size="small"
                showSearch={{ optionFilterProp: 'label' }}
                placeholder="Reference field"
                aria-label="Add reference field"
                value={addRefSlot}
                options={mappingReviewRankedOptions({
                  scored: Object.entries(wholeDoc?.unmappedReferences ?? {})
                    .filter(([key]) => !proposedSlots.has(key))
                    .map(([key, alts]) => ({
                      value: key,
                      probability: Math.max(
                        ...alts.map((a) => a.probability),
                        0,
                      ),
                    })),
                  universe: referenceUniverse.map((slot) => ({
                    value: slot.slotKey,
                    label: proposedSlots.has(slot.slotKey)
                      ? `${slot.label} (proposed)`
                      : slot.label,
                    disabled: proposedSlots.has(slot.slotKey),
                  })),
                  universeLabel: 'All references',
                })}
                onChange={setAddRefSlot}
              />
              {addRefSlot &&
                (() => {
                  const slot = referenceUniverse.find(
                    (s) => s.slotKey === addRefSlot,
                  );
                  if (!slot) return null;
                  return (
                    <ReferenceSelect
                      key={addRefSlot}
                      entityId={slot.entityId}
                      entityGroupId={slot.entityGroupId}
                      value={null}
                      disableManualEntry
                      onChange={(value) => {
                        if (isEntityCellValue(value)) {
                          addReference(
                            slot.slotKey,
                            slot.entityId,
                            {
                              kind: 'entity',
                              docId: value.docId,
                              ...(value.cacheLabel
                                ? { cacheLabel: value.cacheLabel }
                                : {}),
                            },
                            slot.entityGroupId,
                          );
                          setAddRefSlot(undefined);
                        }
                      }}
                    />
                  );
                })()}
            </FlexCol>
          }
        >
          <Button
            size="small"
            icon={<PlusOutlined />}
            aria-label="Add reference"
            disabled={submitting}
          >
            Add reference
          </Button>
        </Popover>
      </FlexBox>
      <div style={{ overflowX: 'auto' }}>
        <FlexCol noGrow noGap>
          <DiffHeader first="Reference field" proposed="Linked record" />
          {references.map((reference) => {
            const slot = referenceUniverse.find(
              (s) => s.slotKey === reference.slotKey,
            );
            const label = slot?.label ?? reference.slotKey;
            const current = currentRefs?.[reference.slotKey];
            const resolved = reference.resolution.kind === 'entity';
            const proposedId = resolved
              ? reference.resolution.docId
              : undefined;
            const changed = current?.entityRecordId !== proposedId;
            const overwrite = Boolean(current?.entityRecordId) && changed;
            const plan = refPlan(reference.slotKey);
            const refCandidates =
              plan && reference.factIndex === plan.factIndex
                ? [...plan.candidates]
                : undefined;
            return (
              <div
                key={reference.slotKey}
                data-slot-key={reference.slotKey}
                style={{
                  display: 'grid',
                  gridTemplateColumns: DIFF_GRID,
                  columnGap: 10,
                  alignItems: 'start',
                  padding: 5,
                  borderRadius: token.borderRadius,
                  background:
                    overwrite && !reference.removed
                      ? token.colorWarningBg
                      : undefined,
                }}
              >
                <FlexCol noGap noGrow>
                  <FlexBox noGrow justifyStart gap={5} alignStart>
                    <Text>{label}</Text>
                    {requiredSlots.has(reference.slotKey) && <RequiredMark />}
                  </FlexBox>
                </FlexCol>
                <div>
                  <CurrentValue
                    current={
                      current?.entityRecordName ?? current?.entityRecordId
                    }
                    changed={changed && !reference.removed}
                  />
                </div>
                <div>
                  {reference.removed ? (
                    <Text delete type="secondary">
                      {resolved
                        ? (reference.resolution.cacheLabel ??
                          reference.resolution.docId)
                        : reference.resolution.text}
                    </Text>
                  ) : (
                    <ReferenceSelect
                      key={`${reference.slotKey}-${proposedId ?? 'unresolved'}`}
                      entityId={reference.entityId}
                      entityGroupId={reference.entityGroupId}
                      value={resolutionToCellValue(reference.resolution)}
                      candidates={refCandidates}
                      disableManualEntry
                      disabled={submitting}
                      onChange={(value) => {
                        if (isEntityCellValue(value)) {
                          setReferenceResolution(reference.slotKey, {
                            kind: 'entity',
                            docId: value.docId,
                            ...(value.cacheLabel
                              ? { cacheLabel: value.cacheLabel }
                              : {}),
                          });
                        }
                      }}
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
                <div>
                  {factOptions.length > 0 && !reference.removed && (
                    <Select
                      size="small"
                      showSearch={{ optionFilterProp: 'label' }}
                      placeholder={mappingReviewLabels.rePickValue}
                      aria-label={`Re-pick source ${mappingReviewLabels.extractedValue} for reference ${label}`}
                      value={
                        reference.factIndex >= 0
                          ? reference.factIndex
                          : undefined
                      }
                      disabled={submitting}
                      options={mappingReviewRankedOptions({
                        scored: refFactScores(reference.slotKey),
                        universe: factOptions,
                        universeLabel: mappingReviewLabels.allValues,
                      })}
                      onChange={(value: number) => {
                        setReferenceFact(reference.slotKey, value);
                        setReferenceResolution(reference.slotKey, {
                          kind: 'manual',
                          text: facts[value]?.value ?? '',
                        });
                      }}
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
                <div>
                  {!resolved && !reference.removed && (
                    <Tooltip
                      title={`Unresolved — extracted "${reference.resolution.kind === 'manual' ? reference.resolution.text : ''}"; search to link a record`}
                    >
                      <Tag color="warning" style={{ margin: 0 }}>
                        unresolved
                      </Tag>
                    </Tooltip>
                  )}
                  {resolved && (
                    <MappingReviewConfidenceTag
                      manual={reference.manual}
                      confidence={refPlanConfidence(reference.slotKey)}
                      aiScore={
                        reference.manual
                          ? refFactScores(reference.slotKey).find(
                              (s) => s.value === reference.factIndex,
                            )?.probability
                          : undefined
                      }
                    />
                  )}
                </div>
                <div>
                  <KeepRejectToggle
                    removed={reference.removed}
                    label={`reference ${label}`}
                    disabled={submitting}
                    onRemove={() => removeReference(reference.slotKey)}
                    onRestore={() => restoreReference(reference.slotKey)}
                  />
                </div>
              </div>
            );
          })}
        </FlexCol>
      </div>
    </FlexCol>
  );
};

export default MappingReviewFields;
